import { EventData } from 'Models'
import clsx from 'clsx'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { QRCodeCanvas } from 'qrcode.react'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { copyToClipBoard } from 'utils/helper/common-helpers'
import { buildVolunteerRegistrationLink } from 'utils/helper/event-volunteer-link'
import { useIsMobile } from 'utils/hooks/useIsMobile'
const QR_DESKTOP_SIZE = 240
const COPY_SUCCESS_RESET_MS = 2000
const QR_MIN_SIZE = 160

type DialogShareVolunteerLinkProps = {
  event: Pick<EventData, '_id' | 'branchId' | 'volunteerToken' | 'title'>
  className?: string
}

const DialogShareVolunteerLink = ({
  event,
  className,
}: DialogShareVolunteerLinkProps) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [visible, setVisible] = useState(false)
  const qrContainerRef = useRef<HTMLDivElement>(null)
  const [qrSize, setQrSize] = useState(QR_DESKTOP_SIZE)
  const [copySucceeded, setCopySucceeded] = useState(false)
  const copyResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const shareLink = useMemo(
    () => buildVolunteerRegistrationLink(event),
    [event]
  )

  useLayoutEffect(() => {
    if (!visible || !shareLink) return

    const el = qrContainerRef.current
    if (!el) return

    const updateSize = () => {
      if (!isMobile) {
        setQrSize(QR_DESKTOP_SIZE)
        return
      }
      const width = el.getBoundingClientRect().width
      const inner = Math.floor(width - 24)
      setQrSize(Math.max(QR_MIN_SIZE, inner))
    }

    updateSize()
    const observer = new ResizeObserver(updateSize)
    observer.observe(el)
    return () => observer.disconnect()
  }, [visible, shareLink, isMobile])

  const clearCopyResetTimer = () => {
    if (copyResetTimerRef.current) {
      clearTimeout(copyResetTimerRef.current)
      copyResetTimerRef.current = null
    }
  }

  useEffect(() => {
    if (!visible) {
      clearCopyResetTimer()
      setCopySucceeded(false)
    }
    return clearCopyResetTimer
  }, [visible])

  const handleCopy = () => {
    if (!shareLink || copySucceeded) return
    copyToClipBoard(shareLink, () => {
      setCopySucceeded(true)
      clearCopyResetTimer()
      copyResetTimerRef.current = setTimeout(() => {
        setCopySucceeded(false)
        copyResetTimerRef.current = null
      }, COPY_SUCCESS_RESET_MS)
    })
  }

  return (
    <>
      <Button
        type="button"
        icon="isax-link-21-bold"
        label={t('Share volunteer link')}
        outlined
        className={clsx('h-fit', className)}
        disabled={!shareLink}
        onClick={() => setVisible(true)}
      />
      <Dialog
        header={t('Volunteer registration link')}
        visible={visible}
        draggable={false}
        resizable={false}
        dismissableMask
        className={clsx(
          isMobile &&
            'mobile-select-branch-dialog m-0 shadow-none border-noround'
        )}
        contentClassName={clsx(
          isMobile && 'h-full flex flex-column overflow-auto'
        )}
        style={
          isMobile
            ? {
                width: '100vw',
                maxWidth: '100vw',
                height: '100vh',
                maxHeight: '100vh',
              }
            : { width: 'min(520px, 95vw)' }
        }
        onHide={() => setVisible(false)}
      >
        {shareLink ? (
          <div
            className={clsx(
              'flex flex-column align-items-center gap-4 w-full',
              isMobile && 'flex-1 justify-content-center p-4'
            )}
          >
            <p className="m-0 text-center text-color-secondary line-height-3 w-full">
              {t('Scan the QR code or copy the link for volunteer check-in.')}
            </p>
            <div
              ref={qrContainerRef}
              className={clsx(
                'p-3 border-1 border-solid border-gray-200 border-round-xl bg-white box-border',
                isMobile ? 'w-full' : 'w-fit mx-auto'
              )}
            >
              <QRCodeCanvas
                value={shareLink}
                size={qrSize}
                level="M"
                includeMargin
                className="block max-w-full h-auto"
                style={{ width: '100%', height: 'auto' }}
              />
            </div>
            <InputText readOnly className="w-full" value={shareLink} />
            <Button
              type="button"
              label={copySucceeded ? t('Copy success') : t('Copy link')}
              icon={copySucceeded ? 'isax-tick-circle' : 'isax-copy'}
              className="w-full h-auto"
              onClick={handleCopy}
            />
          </div>
        ) : (
          <p className="m-0 text-color-secondary">
            {t('Volunteer link is not available for this event.')}
          </p>
        )}
      </Dialog>
    </>
  )
}

export default DialogShareVolunteerLink
